<script>
  import NumberText from '../components/NumberText/index.svelte';

  import { isHighlyComposite } from '../utils/highlyComposite.ts';
  import { isFibonacci } from '../utils/fibonacci.ts';
  import { isHappy } from '../utils/happy.ts';
  import ordinal from '../utils/ordinal.ts';
  import sumOfPalindromes from '../utils/palindromes.js';
  import split, { join, link } from '../utils/split.ts';
  import { isUntouchable } from '../utils/untouchables.ts';
  import { objMap } from '../utils/objects.ts';
  import { roundTo, log } from '../utils/math.ts';
  import { listForms } from '../utils/forms';

  import { links } from '../data/sources';

  import Icon from 'fa-svelte'
  
  import { faChevronDown, faChevronUp, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
  import { toRomanNumerals } from '../utils/romanNumerals';

  import OtherNumbers from '../components/OtherNumbers.svelte';
  import EnterNumber from '../components/EnterNumber.svelte';

  import { getFunctions, httpsCallable } from "firebase/functions";
  const functions = getFunctions();
  const queryDataReq = httpsCallable(functions, 'queryData');
  
  //import { SentenceTokenizer } from 'natural';
  
  // HOLIDAYS

  const holidays = [
    ['Jan 1', 1, "New Year's Day"],
    ['Jan 18', 18, 'Martin Luther King Jr. Day'],
    ['Jan 20', 20, 'Inauguration Day'],
    ['Feb 15', 46, "Presidents' Day"],
    ['May 31', 151, 'Memorial Day'],
    ['Jun 19', 170, 'Juneteenth'],
    ['Jul 4', 185, 'Independence Day'],
    ['Sep 6', 249, 'Labor Day'],
    ['Oct 11', 284, 'Columbus Day'],
    ['Nov 11', 315, 'Veterans Day'],
    ['Nov 25', 329, 'Thanksgiving Day'],
    ['Dec 25', 359, 'Christmas Day'],
    ['Dec 31', 365, "New Year's Eve"]
  ];

  // WORD FORM

  const unfunny = [
    69,
    420,
    6969,
    42069,
    69420,
    420420,
    696969
  ];

  const URL_REGEX = /[-a-zA-Z0-9@:%_\+.~#?&//=]{2,256}\.[a-z]{2,4}\b(\/[-a-zA-Z0-9@:%_\+.~#?&//=]*)?/gi;

  const ones = [
    '',
    'one',
    'two',
    'three',
    'four',
    'five',
    'six',
    'seven',
    'eight',
    'nine',
  ];
  const tens = [
    '',
    '',
    'twenty',
    'thirty',
    'forty',
    'fifty',
    'sixty',
    'seventy',
    'eighty',
    'ninety',
  ];
  const teens = [
    'ten',
    'eleven',
    'twelve',
    'thirteen',
    'fourteen',
    'fifteen',
    'sixteen',
    'seventeen',
    'eighteen',
    'nineteen',
  ];

  const isTriangular = num => Number.isInteger(Math.sqrt((8 * num) + 1));

  const groups = [
    [ 1_000, 'thousand' ],
    [ 1_000_000, 'million' ],
    [ 1_000_000_000, 'billion' ],
    [ 1_000_000_000_000, 'trillion' ],
    [ 1_000_000_000_000_000, 'quadrillion' ],
    [ 1_000_000_000_000_000_000, 'quintillion' ]
  ].reverse();

  const convertAll = num => {
    for (const [ limit, unit ] of groups) {
      if (num >= limit) {
        return convertHigher(num, convertAll, limit, unit);
      }
    }
    return fromHundreds(num);
  }

  const convertHigher = (num, convert, limit, unit) => {
    if (limit > num) {
      return convert(num);
    }
    if (num % limit == 0) {
      return `${convert(Math.floor(num / limit))} ${unit}`;
    }
    return `${convert(Math.floor(num / limit))} ${unit}, ${convert(num % limit)}`;
  };

  const fromThousands = (num) =>
    convertHigher(num, fromHundreds, 1_000, 'thousand');
  const fromMillions = (num) =>
    convertHigher(num, fromThousands, 1_000_000, 'million');
  const fromBillions = (num) =>
    convertHigher(num, fromMillions, 1_000_000_000, 'billion');

  const fromHundreds = (num) => {
    if (99 >= num) {
      return fromTens(num);
    }
    if (num % 100 == 0) {
      return `${fromTens(num / 100)} hundred`;
    }
    const hundreds = Math.floor(num / 100);
    return `${fromTens(hundreds)} hundred and ${fromTens(num % 100)}`;
  };

  const fromTens = (num) => {
    if (num < 10) {
      return ones[num];
    }
    if (num >= 10 && num < 20) {
      return teens[num - 10];
    }
    if ((num % 10) == 0) {
      return tens[Math.floor(num / 10)];
    }
    return `${tens[Math.floor(num / 10)]}-${ones[num % 10]}`;
  };

  const convert = convertAll;

  // UTILS

  const reverseStr = (str) => `${str}`.split('').reverse().join('');
  const isPalindrome = (str) => `${str}` == reverseStr(str);

  const isFactorial = (num) => {
    for (let i = 1; ; i++) {
      if (num % i == 0) {
        num = parseInt(num / i);
      } else {
        break;
      }
    }

    if (num == 1) {
      return true;
    }
    return false;
  };

  const isPrime = (num) => {
    for (let i = 2, s = Math.sqrt(num); i <= s; i++) {
      if (num % i === 0) return false;
    }
    return num > 1;
  };

  const isSemiPrime = (num) => primeFactors(num).length == 2;

  const getFactors = (num) => {
    const isEven = num % 2 === 0;
    const max = Math.sqrt(num);
    const inc = isEven ? 1 : 2;
    let factors = [1, num];

    for (let curFactor = isEven ? 2 : 3; curFactor <= max; curFactor += inc) {
      if (num % curFactor !== 0) continue;
      factors.push(curFactor);
      let compliment = num / curFactor;
      if (compliment !== curFactor) factors.push(compliment);
    }

    return factors;
  };

  const getPrimeFactors = (num) => {
    const factors = [];
    let divisor = 2;

    while (num >= 2) {
      if (num % divisor == 0) {
        factors.push(divisor);
        num = num / divisor;
      } else {
        divisor++;
      }
    }
    return factors;
  };

  const primeFactorization = (num, factors = getPrimeFactors(num)) => {
    const info = {};
    for (const factor of factors) {
      if (info[factor] == null) info[factor] = 0;

      info[factor]++;
    }

    const powers = Object.entries(info)
      .map(([factor, power]) => (power == 1 ? parseInt(factor) : join([ parseInt(factor), power ], '^')));

    return join(powers, ' * ')
  };

  const sumOfFactors = (num) => getFactors(num).reduce((acc, cur) => acc + cur);
  const friendlyRatio = (num) => sumOfFactors(num) / num;

  const isPythagPrime = num => Number.isInteger((num - 1) / 4);

  // Converts a number to null if it is not an int
  const nullInt = (num) => (Number.isInteger(num) ? num : null);

  const timeIt = (label, f) => {
    console.time(label);
    const val = f();
    console.timeEnd(label);
    return val;
  }

  // MAIN STUFF

  export let params;

  $: num = parseFloat(params[0]);

  $: sign = num == 0 ? 'unsigned' : num > 0 ? 'positive' : 'negative';
  $: type = Number.isInteger(num) ? 'integer' : 'decimal number';

  $: details =
    type == 'integer'
      ? split`following ${num - 1} and preceding ${num + 1}`
      : split`which rounds to ${Math.round(num)}`;
  $: form = type == 'integer' ? ` ("${convert(num)}" in words)` : '';

  let properties = [];

  let categories = [];

  $: validNumber = (
    type == 'integer' &&
    sign == 'positive' &&
    5_000_000_000 > num
  );

  $: if (validNumber) {
    categories = [];
    
    // Category: Holidays

    const found = holidays.find(([ ,day ]) => day == num);
    if (found) {
      const [ date,, holiday ] = found;
      const category = {
        name: 'important dates',
        items: [
          split`The ${ordinal(convert(num), num)} day of the year (${date}) is ${holiday}, a federal United States holiday`
        ]
      }
      categories.push(category);
    }

    // Properties

    const prime = isPrime(num);
    let primeFactors;
    try {
      primeFactors = getPrimeFactors(num);
    } catch {
      primeFactors = [];
    }
    const factorization = primeFactorization(num, primeFactors);
    const even = num % 2 == 0;

    const repDigit = `${num}`.split('').every((i) => i == `${num}`[0]);
    const perfectSquare = nullInt(Math.sqrt(num));
    const perfectCube = nullInt(roundTo(num ** (1 / 3), 7));

    const isAbundant = friendlyRatio(num) > 2;
    const palindrome = isPalindrome(num) && `${num}`.length > 1;
    const factorial = isFactorial(num);

    const highlyComposite = 100_000 > num && isHighlyComposite(num);

    const isSophiePrime = prime && isPrime((2 * num) + 1);

    const sophieBelow = (num - 1) / 2;
    const isSafePrime = prime && Number.isInteger(sophieBelow) && isPrime(sophieBelow);

    const twinPrimes = [ num - 2, num + 2 ].filter(isPrime);
    const cousinPrimes = [ num - 4, num + 4 ].filter(isPrime);

    const pythagPrime = isPythagPrime(num);

    const fibonacci = isFibonacci(num);
    const regular = primeFactors.every(i => [ 2, 3, 5 ].includes(i));

    const palindromeSum = sumOfPalindromes(num).map(i => parseInt(i.join('')));
    const happy = isHappy(num);

    const untouchable = 7500 > num && isUntouchable(num);
    const triangular = isTriangular(num);
    const isUnfunny = unfunny.includes(num);

    const mersennePower = log(num + 1, 2);
    const mersenne = Number.isInteger(mersennePower);

    const forms = listForms(num);
    console.log('Forms:', forms);
    const formStr = join(
      forms.map(({ base, power, change }) => join([ base, '^', power, 0 > change ? ' - ' : ' + ', Math.abs(change) ], '')),
      ' and '
    )

    properties = [];
    properties.push(
      split`${num} is an ${link(even ? 'even' : 'odd', 'https://en.wikipedia.org/wiki/Parity_(mathematics)')} ${
        num == 1 ? link('unit', links.unit) : prime ? link('prime', links.prime) : link('composite', links.composite)
      } number`
    );

    if (primeFactors.length > 1)
      properties.push(split`${num}'s ${link('prime factorization', links.primeFactorization)} is ${factorization}`);
    if (`${num}`.length > 1 && repDigit) {
      properties.push(split`${num} is a ${link('repdigit number', links.repdigit)}`);
    } else if (palindrome) {
      properties.push(split`${num} is a ${link('palindromic number', links.palindromic)}`);
    }

    if (palindromeSum.length > 2) {
      properties.push(split`${num} is the ${link('sum of three palindromes', links.threePalindromes)}, being ${join(palindromeSum, ' + ')}`)
    }

    if (perfectSquare && perfectSquare != num) {
      properties.push(split`${num} is a perfect square of ${perfectSquare}`);
    }
    if (perfectCube && perfectCube != num) {
      properties.push(split`${num} is a perfect cube of ${perfectCube}`);
    }

    if (forms.length > 0) {
      properties.push(split`${num} has the representation${forms.length > 1 ? 's' : ''} of ${formStr}`)
    }

    if (primeFactors.length == 2 && primeFactors[0] != primeFactors[1]) {
      properties.push(split`${num} is also a ${link('semiprime number', links.semiprime)}`);
    }

    if (prime) { 
      if (twinPrimes.length > 0) {
        properties.push(split`${num} is a ${link('twin prime', links.twinPrime)} of ${join(twinPrimes, ' and ')}`);
      }

      if (cousinPrimes.length > 0) {
        properties.push(split`${num} is a ${link('cousin prime', links.cousinPrime)} of ${join(cousinPrimes, ' and ')}`);
      }
    }

    if (isSafePrime) {
      properties.push(split`${num} is a ${link('safe prime', links.safePrime)}`);
    }

    if (prime && pythagPrime) {
      properties.push(split`${num} is a ${link('Pythagorean prime', links.pythagoreanPrime)}`);
    }

    if (isSophiePrime) {
      properties.push(split`${num} is a ${link('Sophie Germain prime', links.sophieGermainPrime)}`);
    }

    if (mersenne) {
      properties.push(split`${num} is a ${link(`mersenne ${prime ? 'prime' : 'number'}`, links.mersenne)}`)
    }

    if (isAbundant) {
      properties.push(split`${num} is an ${link('abundant number', links.abundant)}`);
    }

    if (factorial) {
      properties.push(split`${num} is a ${link('perfect factorial', links.factorial)}`);
    }

    if (highlyComposite) {
      properties.push(split`${num} is a ${link('highly composite number', links.highlyComposite)}`);
    }

    if (regular) {
      properties.push(split`${num} is a ${link('regular number', links.regular)}`);
    }

    if (triangular) {
      properties.push(split`${num} is a ${link('triangular number', links.triangular)}`);
    }

    if (happy) {
      properties.push(split`${num} is a ${link('happy number', links.happy)}`);
    }

    if (untouchable) {
      properties.push(split`${num} is an ${link('untouchable number', links.untouchable)}`);
    }

    if (isUnfunny) {
      properties.push(split`You're ${link('not funny', links.unfunny)}`);
    }
  } else {
    // soon
    properties = [];
  }

  const queryData = async num => {
    console.log('Querying firebase!')
    const { data } = await queryDataReq({ num });
    return data;
  }

  const mathFacts = [];

  $: {
    const otherInfo = objMap({
      doubled: num * 2,
      halved: num / 2,
      squared: num ** 2,
      cubed: num ** 3,
      sqrt: Math.sqrt(num),
      cubeRoot: num ** (1/3),
      romanNumerals: 10000 > num ? toRomanNumerals(num) : null
    }, value => typeof value == 'number' ? roundTo(value, 7) : value);

    mathFacts.push(...[
      split`${num} doubled is ${otherInfo.doubled}`,
      split`${num} halved is ${otherInfo.halved}`,
      split`${num} squared is ${otherInfo.squared}`,
      split`${num} cubed is ${otherInfo.cubed}`,
      split`the square root of ${num} is ${otherInfo.sqrt}`,
      split`the cube root of ${num} is ${otherInfo.cubeRoot}`, 
      otherInfo.romanNumerals ? split`${num} is ${otherInfo.romanNumerals} in roman numerals` : null
    ].filter(i => i));
  }

  $: if (num) categories = [ ...categories, queryData(num) ]

  let showInfo = false;
  
  const chevronClick = () => {
    showInfo = !showInfo;
    console.log('Boop!', showInfo)
  }
</script>

<div class="grid grid-cols-4 h-screen w-screen">
  <div class = "bg-gray-100">
    <div class = "m-10">
      <EnterNumber />
    </div>
  </div>
  <div class = "col-span-2">
    <div class = "m-10">
      {#if validNumber}
        <h1>
          {num}
        </h1>

        <b>{num}</b>{form} is a {sign} {type} <NumberText info = {details} />.

        <h2>
          Properties
        </h2>

        <ul>
          {#each properties as prop} 
            <li><NumberText info = {prop} /></li>
          {/each}
        </ul>

        <h2>
          Other Math Info <span class = "hoverable" on:click = {chevronClick}><Icon icon = {showInfo ? faChevronDown : faChevronRight} /></span>
        </h2>

        {#if showInfo}
          <ul>
            {#each mathFacts as fact}
              <li><NumberText info = {fact} /></li>
            {/each}
          </ul>
        {/if}

        {#each categories as undoneCategory}
          {#await undoneCategory then category}
            {#if category}
              <h2>In {category.name}</h2>
              <ul>
                {#each category.items as item}
                  {#if item && item.link}
                    <li><a class = "as-text" href = {item.link}><NumberText info = {item.text} /></a></li>
                  {:else}
                    <li><NumberText info = {item} /></li>
                  {/if}
                {/each}
              </ul>
            {/if}
          {/await}
        {/each}
      {:else}
        <h1>
          Invalid Number
        </h1>

        <p>
          <b>{num}</b> is unfortunately not valid.
        </p>

        <p>It is either:</p>
        <ul>
          <li>Not a whole number</li>
          <li>Not a positive number</li>
          <li>Too large (greater than or equal to <b>five billion</b>)</li>
        </ul>
      {/if}
    </div>
  </div>
  <div class = "bg-gray-100">
    <div class = "m-10">
      <OtherNumbers />
      <br />
      <p><a href = "/about">Created by Corman.</a></p>
    </div>
  </div>
</div>